import type { MapFeature, WirePosition } from '../api/types'

/** What the editor can change about a feature. Geometry included, for moving and reshaping. */
export interface FeatureEdits {
  label: string
  remarks: string
  colorHex: string | null
  visibility: MapFeature['visibility']
  objective: boolean
  positions: WirePosition[]
  radiusMetres?: number
}

export function editsOf(feature: MapFeature): FeatureEdits {
  return {
    label: feature.label ?? '',
    remarks: feature.remarks ?? '',
    colorHex: feature.colorHex ?? null,
    visibility: feature.visibility,
    objective: feature.objective,
    positions: feature.positions.map((p) => ({ latitude: p.latitude, longitude: p.longitude })),
    radiusMetres: feature.radiusMetres,
  }
}

export function isDirty(feature: MapFeature, edits: FeatureEdits): boolean {
  return JSON.stringify(editsOf(feature)) !== JSON.stringify(edits)
}

/**
 * The whole feature for a PUT, with the edits applied.
 *
 * The server replaces a feature rather than patching it: a field left out of the body is a field
 * cleared. So every property the editor does not touch -- the tactical symbol, its status and
 * echelon, stroke and fill, attachments -- is carried over from what was loaded, or saving a new
 * label would quietly strip the symbol off a unit marker.
 */
export function saveBody(feature: MapFeature, edits: FeatureEdits): Record<string, unknown> {
  return {
    geometryType: feature.geometryType,
    positions: edits.positions,
    radiusMetres: feature.geometryType === 'CIRCLE' ? edits.radiusMetres : undefined,
    visibility: edits.visibility,
    symbol: feature.symbol,
    affiliation: feature.affiliation,
    symbolStatus: feature.symbolStatus,
    echelon: feature.echelon,
    label: edits.label.trim() || undefined,
    objective: edits.objective,
    remarks: edits.remarks.trim() || undefined,
    colorHex: edits.colorHex ?? undefined,
    strokeWidth: feature.strokeWidth,
    fillOpacity: feature.fillOpacity,
    dashed: feature.dashed ?? false,
    attachmentMediaIds: feature.attachmentMediaIds ?? [],
  }
}

/** What to call a feature with no name, by its shape. */
export function kindName(feature: Pick<MapFeature, 'geometryType' | 'objective'>): string {
  if (feature.objective) return 'Objective'
  switch (feature.geometryType) {
    case 'POINT':
      return 'Marker'
    case 'POLYLINE':
      return 'Route'
    case 'POLYGON':
      return 'Area'
    case 'RECTANGLE':
      return 'Box'
    case 'CIRCLE':
      return 'Range ring'
    case 'MEASUREMENT':
      return 'Measurement'
  }
}
